/**
 * Utilidades para llamadas a la API
 * @module utils/api
 */

/**
 * Realiza una llamada a la API con autenticación
 * @async
 * @param {string} endpoint - Endpoint de la API (sin base URL)
 * @param {Object} options - Opciones de fetch
 * @param {string} options.method - Método HTTP (default: 'GET')
 * @param {Object} options.body - Body de la petición
 * @param {Object} options.headers - Headers adicionales
 * @param {boolean} options.requireAuth - Requiere autenticación (default: true)
 * @param {boolean} options.requireType - Incluir header X-WETAG-Type (default: true)
 * @returns {Promise<Object>} Respuesta parseada como JSON
 * @throws {Error} Si la respuesta no es OK o hay error de red
 */
export async function apiCall(endpoint, options = {}) {
    if (!window.App || !window.App.settings || !window.App.settings.restUrl) {
        throw new Error('App settings not available');
    }

    const {
        method = 'GET',
        body = null,
        headers = {},
        requireAuth = true,
        requireType = true
    } = options;

    let url = endpoint.startsWith('http') 
        ? endpoint 
        : `${window.App.settings.restUrl}${endpoint}`;

    const requestHeaders = { ...headers };
    if (requireType) {
        requestHeaders['X-WETAG-Type'] = 'members';
    }

    if (requireAuth) {
        if (!window.App.auth || !window.App.auth.token) {
            throw new Error('Authentication token not available');
        }
        requestHeaders['X-WETAG-Token'] = window.App.auth.token;
    }

    try {
        const fetchOptions = {
            method,
            headers: requestHeaders,
            cache: 'no-cache'
        };

        if (body) {
            if (body instanceof FormData) {
                fetchOptions.body = body;
            } else if (body instanceof URLSearchParams) {
                fetchOptions.body = body;
            } else {
                fetchOptions.body = JSON.stringify(body);
                requestHeaders['Content-Type'] = 'application/json';
            }
        }

        const urlObj = new URL(url);
        if (urlObj.searchParams.has('reload')) {
            urlObj.searchParams.set('reload', Date.now().toString());
            url = urlObj.toString();
        }

        const response = await fetch(url, fetchOptions);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseText = await response.text();
        
        if (!responseText || responseText.trim() === '') {
            return { class: 'success', html: '' };
        }
        
        try {
            const data = JSON.parse(responseText);
            return data;
        } catch (parseError) {
            return { class: 'success', html: responseText, raw: responseText };
        }
    } catch (error) {
        throw error;
    }
}

/**
 * Realiza una llamada GET a la API
 * @async
 * @param {string} endpoint - Endpoint de la API
 * @param {Object} options - Opciones adicionales
 * @returns {Promise<Object>} Respuesta parseada
 */
export async function apiGet(endpoint, options = {}) {
    return apiCall(endpoint, { ...options, method: 'GET' });
}

/**
 * Realiza una llamada POST a la API
 * @async
 * @param {string} endpoint - Endpoint de la API
 * @param {Object|FormData} body - Body de la petición
 * @param {Object} options - Opciones adicionales
 * @returns {Promise<Object>} Respuesta parseada
 */
export async function apiPost(endpoint, body, options = {}) {
    return apiCall(endpoint, { ...options, method: 'POST', body });
}

/**
 * Realiza una llamada DELETE a la API
 * @async
 * @param {string} endpoint - Endpoint de la API
 * @param {Object} options - Opciones adicionales
 * @returns {Promise<Object>} Respuesta parseada
 */
export async function apiDelete(endpoint, options = {}) {
    return apiCall(endpoint, { ...options, method: 'DELETE' });
}
